import React, { useState, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import couponService from "../../services/couponService";

const CouponFormModal = ({ isOpen, onClose, onSave, coupon, isSaving }) => {
  const [formData, setFormData] = useState({
    code: "",
    discount_percentage: "",
    expiration_date: "",
    description: "",
    active: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code || "",
        discount_percentage: String(coupon.discount_percentage || ""),
        expiration_date:
          couponService.formatDateForInput(coupon.expiration_date) || "",
        description: coupon.description || "",
        active: coupon.active !== undefined ? coupon.active : true,
      });
    } else {
      setFormData({
        code: "",
        discount_percentage: "",
        expiration_date: "",
        description: "",
        active: true,
      });
    }
    setErrors({});
  }, [coupon, isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = "Código é obrigatório";
    } else if (formData.code.length > 20) {
      newErrors.code = "Código deve ter no máximo 20 caracteres";
    }

    const discount = parseFloat(formData.discount_percentage);
    if (!formData.discount_percentage || isNaN(discount)) {
      newErrors.discount_percentage = "Desconto é obrigatório";
    } else if (discount <= 0) {
      newErrors.discount_percentage = "Desconto deve ser maior que zero";
    } else if (discount > 100) {
      newErrors.discount_percentage = "Desconto não pode ser maior que 100%";
    }

    if (!formData.expiration_date) {
      newErrors.expiration_date = "Data de expiração é obrigatória";
    } else {
      const selectedDate = new Date(formData.expiration_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today && !coupon) {
        newErrors.expiration_date = "Data não pode ser anterior a hoje";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      code: formData.code.trim().toUpperCase(),
      discount_percentage: parseFloat(formData.discount_percentage),
      expiration_date: formData.expiration_date,
      description: formData.description.trim(),
      active: formData.active,
    };

    onSave(payload);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{coupon ? "Editar Cupom" : "Novo Cupom"}</DialogTitle>
          <DialogDescription>
            {coupon
              ? "Atualize as informações do cupom."
              : "Preencha os dados para criar um novo cupom de desconto."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Código */}
          <div className="space-y-2">
            <Label htmlFor="code">
              Código do Cupom <span className="text-red-500">*</span>
            </Label>
            <Input
              id="code"
              placeholder="Ex: BONFIM7"
              value={formData.code}
              onChange={(e) =>
                handleChange("code", e.target.value.toUpperCase())
              }
              className={errors.code ? "border-red-500" : ""}
              maxLength={20}
            />
            {errors.code && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.code}
              </p>
            )}
            <p className="text-xs text-gray-500">
              Código único para identificar o cupom
            </p>
          </div>

          {/* Desconto */}
          <div className="space-y-2">
            <Label htmlFor="discount_percentage">
              Desconto (%) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="discount_percentage"
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              placeholder="Ex: 7.00"
              value={formData.discount_percentage}
              onChange={(e) =>
                handleChange("discount_percentage", e.target.value)
              }
              className={errors.discount_percentage ? "border-red-500" : ""}
            />
            {errors.discount_percentage && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.discount_percentage}
              </p>
            )}
            <p className="text-xs text-gray-500">
              Percentual de desconto que será aplicado
            </p>
          </div>

          {/* Data de Expiração */}
          <div className="space-y-2">
            <Label htmlFor="expiration_date">
              Data de Expiração <span className="text-red-500">*</span>
            </Label>
            <Input
              id="expiration_date"
              type="date"
              value={formData.expiration_date}
              onChange={(e) => handleChange("expiration_date", e.target.value)}
              className={errors.expiration_date ? "border-red-500" : ""}
            />
            {errors.expiration_date && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.expiration_date}
              </p>
            )}
            <p className="text-xs text-gray-500">
              Data até a qual o cupom será válido
            </p>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              placeholder="Ex: Cupom de Natal - 7% de desconto"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-gray-500">
              Descrição opcional para identificar o cupom (
              {formData.description.length}/200)
            </p>
          </div>

          {/* Status Ativo */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="active">Cupom Ativo</Label>
              <p className="text-sm text-gray-500">
                {formData.active
                  ? "Cupom disponível para uso"
                  : "Cupom desativado temporariamente"}
              </p>
            </div>
            <Switch
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => handleChange("active", checked)}
            />
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              style={{ background: "#1b5e20" }}
              className="hover:opacity-90"
            >
              {isSaving ? "Salvando..." : coupon ? "Atualizar" : "Criar Cupom"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CouponFormModal;
